import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useWindowWidth } from "../../utils/useWidth";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "../../assets/img/logo.png";
import { structures } from "../../data/structuresData";

const useSmoothScrollNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleHashNavigation = (e, hash) => {
    e.preventDefault();
    const isOnHomePage = location.pathname === "/";
    if (isOnHomePage) {
      const element = document.querySelector(hash);
      if (element) element.scrollIntoView({ behavior: "smooth" });
      window.history.pushState(null, "", hash);
    } else {
      navigate("/" + hash);
    }
  };

  return { handleHashNavigation };
};

const getNavItems = (t) => {
  const structureChildren = structures.map((s) => ({
    id: `structure-${s.id}`,
    name: s.fullName || s.name,
    link: `/structures/${s.id}`,
  }));

  return [
    { id: 0, name: t("nav.home"), link: "/", type: "primary" },
    { id: 1, name: t("nav.about"), link: "/about", type: "primary" },
    { id: 3, name: t("nav.structures"), type: "dropdown", children: structureChildren },
    { id: 4, name: t("nav.gallery"), link: "/gallery", type: "primary" },
    { id: 5, name: t("nav.cinqueTerre"), link: "/cinque-terre", type: "primary" },
    { id: 6, name: t("nav.touristInfo"), link: "/tourist-informations", type: "primary" },
    { id: 7, name: t("nav.bookNow"), link: "/camere", type: "cta" },
  ];
};

const ChevronDown = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" clipRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" />
  </svg>
);

const ChevronRight = ({ className }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" clipRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" />
  </svg>
);

function DesktopDropdown({ item, isScrolled, onHashClick }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveSubmenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-x-1 transition duration-300 hover:text-lux-accent"
      >
        {item.name}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`absolute left-0 mt-2 w-48 origin-top-left rounded-sm bg-[#1b1814] ring-1 ring-[#c9a962]/30 shadow-xl transition-all duration-150 ${
          isOpen ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"
        }`}
      >
        <div className="py-1">
          {item.children.map((child) => (
            <div
              key={child.id}
              className="relative"
              onMouseEnter={() => setActiveSubmenu(child.id)}
              onMouseLeave={() => setActiveSubmenu(null)}
            >
              {child.children ? (
                <>
                  <button className="w-full flex items-center justify-between px-4 py-2 text-sm text-white/90 hover:bg-[#c9a962]/15 hover:text-white transition-colors">
                    {child.name}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div
                    className={`absolute left-full top-0 ml-1 w-44 rounded-sm bg-[#1b1814] ring-1 ring-[#c9a962]/30 shadow-xl transition-all duration-150 ${
                      activeSubmenu === child.id ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"
                    }`}
                  >
                    <div className="py-1">
                      {child.children.map((subChild) =>
                        subChild.link?.startsWith("#") ? (
                          <a
                            key={subChild.id}
                            href={subChild.link}
                            onClick={(e) => { onHashClick(e, subChild.link); setIsOpen(false); setActiveSubmenu(null); }}
                            className="block px-4 py-2 text-sm text-white/90 hover:bg-[#c9a962]/15 hover:text-white transition-colors"
                          >
                            {subChild.name}
                          </a>
                        ) : (
                          <Link
                            key={subChild.id}
                            to={subChild.link}
                            onClick={() => { setIsOpen(false); setActiveSubmenu(null); }}
                            className="block px-4 py-2 text-sm text-white/90 hover:bg-[#c9a962]/15 hover:text-white transition-colors"
                          >
                            {subChild.name}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </>
              ) : child.link?.startsWith("#") ? (
                <a
                  href={child.link}
                  onClick={(e) => { onHashClick(e, child.link); setIsOpen(false); }}
                  className="block px-4 py-2 text-sm text-white/90 hover:bg-[#c9a962]/15 hover:text-white transition-colors"
                >
                  {child.name}
                </a>
              ) : (
                <Link
                  to={child.link}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-2 text-sm text-white/90 hover:bg-[#c9a962]/15 hover:text-white transition-colors"
                >
                  {child.name}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileDrawer({ isOpen, onClose, vaiEChiudi, t }) {
  const location = useLocation();
  const [structuresOpen, setStructuresOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) setStructuresOpen(false);
  }, [isOpen]);

  const isActive = (link) => {
    if (!link || link.startsWith("#")) return false;
    if (link === "/") return location.pathname === "/";
    return location.pathname === link || location.pathname.startsWith(link + "/");
  };

  // Le voci compaiono una dopo l'altra dopo che il pannello ha finito di entrare.
  const listaVoci = {
    nascosto: {},
    visibile: { transition: { staggerChildren: 0.045, delayChildren: 0.18 } },
  };

  const voce = {
    nascosto: { opacity: 0, scale: 0.97 },
    visibile: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const Divider = () => (
    <div className="h-px w-full" style={{ background: 'linear-gradient(to right, rgba(138,116,88,0.3), transparent)' }} />
  );

  const itemClass = (link) =>
    isActive(link)
      ? "block text-lux-accent text-lg py-4 border-l-2 border-lux-accent pl-3 transition-colors duration-200"
      : "block text-[#f5f1e8] hover:text-lux-accent text-lg py-4 pl-6 transition-colors duration-200";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 bg-black/50 z-[50]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{
              x: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
              opacity: { duration: 0.45, ease: "easeOut" },
            }}
            className="fixed right-0 top-0 h-full w-64 bg-[#14120f] border-l border-[#2e2921] text-[#f5f1e8] z-[60] flex flex-col"
            // L'header applica un'ombra al testo quando sta sopra l'hero scuro
            // e il drawer, essendogli dentro, se la ereditava.
            // willChange promuove il pannello a livello suo: su mobile evita
            // che il browser ridisegni tutta la pagina a ogni fotogramma.
            style={{ textShadow: "none", willChange: "transform, opacity" }}
          >
            {/* Spazio per il tasto dell'header, che ora fa anche da chiusura. */}
            <div className="h-14 shrink-0" />

            {/* Nav links */}
            <motion.nav
              className="flex-1 overflow-y-auto"
              variants={listaVoci}
              initial="nascosto"
              animate="visibile"
            >
              <motion.div variants={voce}>
                <Link to="/" onClick={() => vaiEChiudi("/")} className={itemClass("/")}>
                  {t("nav.home")}
                </Link>
                <Divider />
              </motion.div>

              <motion.div variants={voce}>
                <Link to="/about" onClick={() => vaiEChiudi("/about")} className={itemClass("/about")}>
                  {t("nav.about")}
                </Link>
                <Divider />
              </motion.div>

              {/* Structures accordion */}
              <motion.div variants={voce}>
                <button
                  onClick={() => setStructuresOpen(!structuresOpen)}
                  className="flex items-center justify-between w-full text-[#f5f1e8] hover:text-lux-accent text-lg py-4 pl-6 pr-4 transition-colors duration-200"
                >
                  {t("nav.structures")}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${structuresOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {structuresOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                      className="overflow-hidden bg-[#1b1814]"
                    >
                      <Link
                        to="/structures/cavour"
                        onClick={() => vaiEChiudi("/structures/cavour")}
                        className={
                          isActive("/structures/cavour")
                            ? "block text-lux-accent text-base py-3 border-l-2 border-lux-accent pl-6 transition-colors duration-200"
                            : "block text-[#f5f1e8] hover:text-lux-accent text-base py-3 pl-9 transition-colors duration-200"
                        }
                      >
                        New Moon Cavour
                      </Link>
                      <Divider />
                      <Link
                        to="/structures/prione"
                        onClick={() => vaiEChiudi("/structures/prione")}
                        className={
                          isActive("/structures/prione")
                            ? "block text-lux-accent text-base py-3 border-l-2 border-lux-accent pl-6 transition-colors duration-200"
                            : "block text-[#f5f1e8] hover:text-lux-accent text-base py-3 pl-9 transition-colors duration-200"
                        }
                      >
                        New Moon Prione
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
                <Divider />
              </motion.div>

              <motion.div variants={voce}>
                <Link
                  to="/gallery"
                  onClick={() => vaiEChiudi("/gallery")}
                  className={itemClass("/gallery")}
                >
                  {t("nav.gallery")}
                </Link>
                <Divider />
              </motion.div>

              <motion.div variants={voce}>
                <Link to="/cinque-terre" onClick={() => vaiEChiudi("/cinque-terre")} className={itemClass("/cinque-terre")}>
                  {t("nav.cinqueTerre")}
                </Link>
                <Divider />
              </motion.div>

              <motion.div variants={voce}>
                <Link to="/tourist-informations" onClick={() => vaiEChiudi("/tourist-informations")} className={itemClass("/tourist-informations")}>
                  {t("nav.touristInfo")}
                </Link>
                <Divider />
              </motion.div>
            </motion.nav>

            {/* Bottom: CTA + Language switcher */}
            <div className="h-px w-full shrink-0" style={{ background: 'linear-gradient(to right, transparent, rgba(138,116,88,0.3), transparent)' }} />
            <div className="px-6 py-6 flex flex-col items-center gap-4 shrink-0 overflow-visible relative">
              <Link
                to="/camere"
                onClick={onClose}
                className="w-full text-center bg-lux-accent-strong hover:bg-lux-highlight text-[#161310] font-semibold py-3 px-8 rounded-sm transition-all duration-300 hover:brightness-110"
              >
                {t("nav.bookNow")}
              </Link>
              <LanguageSwitcher dropUp />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Quando si cambia pagina non va ripristinata la vecchia posizione.
  const saltaRipristino = useRef(false);
  const width = useWindowWidth();
  const breakpoint = 1024;
  const location = useLocation();
  const { handleHashNavigation } = useSmoothScrollNav();

  // Notte d'Oro: tutto il sito e' scuro, la barra parte trasparente
  // ovunque e prende il fondo pieno solo quando si scorre.
  const heroScura = true;
  const barraChiara = isScrolled || !heroScura;
  // Su mobile, a menu aperto, il fondo della barra sparisce per non
  // sovrapporsi al pannello.
  // Su mobile la barra resta sempre col fondo pieno: trasparente sopra i
  // contenuti scuri sembrava sparire.
  const fondoPieno =
    (barraChiara || width <= breakpoint) && !(width <= breakpoint && isOpen);

  const navItems = getNavItems(t);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, []);

  // Blocco dello scorrimento a menu aperto. Il body viene fissato all'altezza
  // corrente e alla chiusura si ripristina la posizione: con height/overflow
  // il documento si accorciava e il browser riportava in cima alla pagina.
  useEffect(() => {
    if (!isOpen) return;
    const posizione = window.scrollY;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${posizione}px`;
    body.style.left = "0";
    body.style.right = "0";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      if (saltaRipristino.current) {
        // Si sta andando su un'altra pagina: si resta in cima.
        saltaRipristino.current = false;
        window.scrollTo(0, 0);
        return;
      }
      window.scrollTo(0, posizione);
    };
  }, [isOpen]);

  // Close drawer on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const closeMenu = () => setIsOpen(false);

  // Chiusura del menu da una voce: se punta alla pagina in cui siamo gia',
  // segnaliamo al ripristino dello scorrimento di riportare in cima.
  const chiudiDaVoce = (link) => {
    if (link !== location.pathname) {
      saltaRipristino.current = true;
      setIsOpen(false);
      return;
    }
    setIsOpen(false);
    // Si aspetta che il pannello sia uscito e che il blocco dello scorrimento
    // sia stato tolto, poi si sale in modo morbido come sul desktop.
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 420);
  };

  // Cliccando la voce della pagina in cui sei, l'indirizzo non cambia e
  // ScrollToTop non scatta: qui riportiamo su a mano.
  const tornaSuSeStessaPagina = (link) => {
    if (link === location.pathname) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      className="fixed top-0 left-0 w-full z-[1000] flex items-center justify-between h-[calc(3.5rem+env(safe-area-inset-top))] md:h-[calc(4rem+env(safe-area-inset-top))] pt-[calc(0.75rem+env(safe-area-inset-top))] pb-3 px-6 lg:px-24"
      style={{
        textShadow: barraChiara ? "none" : "0 1px 6px rgba(0,0,0,0.65)",
        transition: "text-shadow 300ms ease",
      }}
    >
      {/* Il fondo pieno e' un livello a se': cosi' entra e esce in dissolvenza
          invece di comparire di colpo al primo pixel di scorrimento. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-[#12100d]/90 shadow-sm border-b border-lux-muted transition-opacity duration-300 ease-out ${
          fondoPieno ? "opacity-100 backdrop-blur-md" : "opacity-0"
        }`}
      />
      <div className="logo relative flex space-x-2 items-center min-w-0 flex-shrink">
        <img
          src={Logo}
          alt="Logo"
          className="w-8 h-8 object-contain flex-shrink-0 transition-all duration-300"
        />
        <Link
          to="/"
          onClick={() => tornaSuSeStessaPagina("/")}
          className={`cormorant font-semibold text-2xl lg:text-[26px] tracking-[0.06em] cursor-pointer whitespace-nowrap transition-colors duration-300 ${
            barraChiara ? "text-[#f5f1e8]" : "text-[#aea07a]"
          }`}
        >
          New Moon
        </Link>
      </div>

      {width < breakpoint ? (
        <>
          {/* Niente riquadri: bandiera e menu prendono il colore della barra
              e sopra l'hero scuro si staccano con una piccola ombra. */}
          <div
            className={`relative flex items-center gap-2 transition-colors duration-300 ${
              barraChiara || isOpen
                ? "text-[#f5f1e8]"
                : "text-[#e3caa0] drop-shadow-[0_1px_3px_rgba(0,0,0,0.75)]"
            }`}
          >
            <LanguageSwitcher compact />
            {/* Le tre linee diventano una X. Il tasto sta sopra al pannello
                (z maggiore) cosi' resta visibile e serve anche a chiudere. */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative z-[70] flex items-center justify-center w-[38px] h-[38px] transition-opacity duration-200 hover:opacity-75"
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
            >
              {/* Tutto il gruppo compie mezzo giro mentre le linee si
                  incrociano: cosi' si legge come una rotazione unica e non
                  come un'icona sostituita da un'altra. */}
              <motion.span
                className="relative block w-6 h-[18px]"
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                {[
                  { top: 0, aperto: { rotate: 45, y: 8 } },
                  { top: 8, aperto: { opacity: 0, scaleX: 0.2 } },
                  { top: 16, aperto: { rotate: -45, y: -8 } },
                ].map((linea, i) => (
                  <motion.span
                    key={i}
                    className="absolute left-0 block h-[2px] w-full bg-current rounded-full"
                    style={{ top: linea.top, transformOrigin: "center" }}
                    animate={
                      isOpen
                        ? linea.aperto
                        : { rotate: 0, y: 0, opacity: 1, scaleX: 1 }
                    }
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  />
                ))}
              </motion.span>
            </button>
          </div>

          <MobileDrawer
            isOpen={isOpen}
            onClose={closeMenu}
            vaiEChiudi={chiudiDaVoce}
            t={t}
          />
        </>
      ) : (
        <div
          className={`buttons relative flex items-center space-x-4 cormorant font-medium text-[18px] tracking-[0.04em] transition-colors duration-300 ${
            barraChiara ? "text-lux-cream" : "text-white"
          }`}
        >
          <LanguageSwitcher />
          {navItems.map((item) => (
            <div key={item.id}>
              {item.type === "dropdown" ? (
                <DesktopDropdown item={item} isScrolled={isScrolled} onHashClick={handleHashNavigation} />
              ) : item.type === "cta" ? (
                <Link to={item.link} className="group transition duration-300">
                  <div className="bg-lux-accent-strong hover:bg-lux-highlight px-5 py-2.5 rounded-sm text-[#161310] font-medium">
                    {item.name}
                  </div>
                </Link>
              ) : item.link?.startsWith("#") ? (
                <a
                  href={item.link}
                  onClick={(e) => handleHashNavigation(e, item.link)}
                  className="group transition duration-300 hover:text-lux-accent"
                >
                  {item.name}
                  <span className="block max-w-0 group-hover:max-w-full transition-all duration-300 h-0.5 bg-lux-accent"></span>
                </a>
              ) : (
                <Link
                  to={item.link}
                  onClick={() => tornaSuSeStessaPagina(item.link)}
                  className="group transition duration-300 hover:text-lux-accent"
                >
                  {item.name}
                  <span className="block max-w-0 group-hover:max-w-full transition-all duration-300 h-0.5 bg-lux-accent"></span>
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
